import React, { useEffect, useState, useMemo } from "react";
import {
	Radio,
	RadioGroup,
	FormControlLabel,
	Card,
	CardContent,
	Grid,
	TextField,
	FormControl,
	Autocomplete,
	Icon,
	Tooltip,
	Box,
} from "../../../common/MaterialComponent";
import Shared from "../../../common/shared";
import {
	GetCountryByPreSubmissionConfigurationId,
	GetIncomingPhoneNumbers,
} from "../../../api/batchNotificationController";
import "./MessageSenderConfiguration.css";
import CountryModel from "../../../models/Country/CountryModel";
import PhoneNumbers from "../../../models/BatchMessage/PhoneNumbersModel";
import {
	AlphanumericSupport,
	BatchMessageTypeEnum,
} from "../../../common/enum";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import BatchMessageDetailModel from "../../../models/BatchMessage/BatchMessageDetailModel";
import { LinkPrimary } from "../../../common/sharedStyle";

export interface MessageSenderConfigurationProp {
	batchNotificationDetail: BatchMessageDetailModel;
	onBatchNotificationChange: React.Dispatch<
		React.SetStateAction<BatchMessageDetailModel>
	>;
	setHasStep5ConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
	highlightRequiredDetail: boolean;
	isReadOnly: boolean;
}

export const MessageSenderConfiguration: React.FC<MessageSenderConfigurationProp> = ({
	batchNotificationDetail,
	isReadOnly,
	onBatchNotificationChange,
	highlightRequiredDetail,
	setHasStep5ConfigChanged,
}) => {
	const strings = useGetComponentStringsContext().MessageSenderConfiguration;
	const [phoneNumberList, setPhoneNumberList] = useState<PhoneNumbers[]>([]);
	const [countryDetail, setCountryDetail] = useState<CountryModel>(
		new CountryModel()
	);
	const [fromNumber, setFromNumber] = useState<PhoneNumbers | null>(null);
	const [alphaNumericFromNumber, setAlphaNumericFromNumber] = useState<string>(
		""
	);

	const [whatsAppFromNumber, setWhatsAppFromNumber] = useState<string>("");
	const [noPhoneNumberAvailable, setNoPhoneNumberAvailable] = useState<boolean>(
		false
	);
	const [isPhoneNumberLoaded, setIsPhoneNumberLoaded] = useState(false);
	const [isCountryDetailLoaded, setIsCountryDetailLoaded] = useState(false);

	const isBatchMessageTypeSMS = useMemo(
		() => () => {
			return (
				batchNotificationDetail.batchMessageType &&
				batchNotificationDetail.batchMessageType === BatchMessageTypeEnum.SMS
			);
		},
		[batchNotificationDetail.batchMessageType]
	);

	const isAlphanumericSenderIdSet = useMemo(
		() => () => {
			return (
				isBatchMessageTypeSMS() &&
				(countryDetail.alphanumericSupport === AlphanumericSupport.Dynamic ||
					countryDetail.alphanumericSupport ===
						AlphanumericSupport.RegistrationRequired) &&
				batchNotificationDetail.isAlphaNumericSenderId
			);
		},
		[
			isBatchMessageTypeSMS,
			countryDetail.alphanumericSupport,
			batchNotificationDetail.isAlphaNumericSenderId,
		]
	);

	const isBatchMessageTypeWhatsApp = useMemo(() => {
		return (
			batchNotificationDetail.batchMessageType &&
			batchNotificationDetail.batchMessageType === BatchMessageTypeEnum.WhatsApp
		);
	}, [batchNotificationDetail.batchMessageType]);

	const messageSenderDetailChange = (
		fromNumber: string,
		isAlphaNumericSenderId: boolean
	) => {
		onBatchNotificationChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				fromNumber: fromNumber,
				isAlphaNumericSenderId: isAlphaNumericSenderId,
			};
		});
		setHasStep5ConfigChanged(true);
	};

	useEffect(() => {
		if (batchNotificationDetail.africasTalkingEnabled) {
			// set from number for Africa's Talking API
			setAlphaNumericFromNumber(batchNotificationDetail.fromNumber);
			return;
		}
		if (isBatchMessageTypeWhatsApp) {
			// set whatsapp configure from number
			setWhatsAppFromNumber(batchNotificationDetail.fromNumber);
			return;
		}
		const handlePhoneNumberAPIResponse = (phoneNumberList: PhoneNumbers[]) => {
			const index = batchNotificationDetail.fromNumber
				? phoneNumberList.findIndex(
						(p: PhoneNumbers) =>
							p.phoneNumber === batchNotificationDetail.fromNumber
				  )
				: -1;
			setPhoneNumberList(phoneNumberList);
			setNoPhoneNumberAvailable(phoneNumberList.length === 0 ? true : false);
			const number =
				!batchNotificationDetail.fromNumber && phoneNumberList.length === 1
					? phoneNumberList[0]
					: index > -1
					? (phoneNumberList[index] as PhoneNumbers)
					: null;
			setFromNumber(number);
			onBatchNotificationChange((prevBatchMessageDetail) => {
				return {
					...prevBatchMessageDetail,
					fromNumber: number ? number.phoneNumber : "",
					isAlphaNumericSenderId: isBatchMessageTypeSMS()
						? batchNotificationDetail.isAlphaNumericSenderId
						: false,
				};
			});
			if (number && !batchNotificationDetail.fromNumber) {
				setHasStep5ConfigChanged(true);
			}
		};

		const getIncomingPhoneNumbers = async () => {
			try {
				const res = await GetIncomingPhoneNumbers(
					batchNotificationDetail.id,
					batchNotificationDetail.partitionKey
				);
				if (res.responseObj) {
					handlePhoneNumberAPIResponse(res.responseObj);
				} else {
					Shared.showSnackbarMessage(strings.phoneNumberListErrorMessage);
				}
				if (batchNotificationDetail.isAlphaNumericSenderId) {
					setAlphaNumericFromNumber(batchNotificationDetail.fromNumber);
				}
				setIsPhoneNumberLoaded(true);
			} catch (err) {
				if (batchNotificationDetail.isAlphaNumericSenderId) {
					setAlphaNumericFromNumber(batchNotificationDetail.fromNumber);
				}
				Shared.showSnackbarMessage(strings.phoneNumberListErrorMessage);
				setIsPhoneNumberLoaded(true);
			}
		};
		if (!isPhoneNumberLoaded) {
			getIncomingPhoneNumbers();
		}
		return () => {
			setIsPhoneNumberLoaded(true);
		};
		// disabled eslint rule as API needs to be called once on page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (batchNotificationDetail.africasTalkingEnabled) {
			return;
		}
		const handleCountryDetailAPIResponse = (countryModel: CountryModel) => {
			setCountryDetail(countryModel);
			if (!batchNotificationDetail.fromNumber) {
				onBatchNotificationChange((prevBatchMessageDetail) => {
					return {
						...prevBatchMessageDetail,
						isAlphaNumericSenderId:
							countryModel.alphanumericSupport !== AlphanumericSupport.None
								? true
								: false,
					};
				});
			}
		};

		const getCountryByPreSubmissionConfigurationId = async () => {
			try {
				const res = await GetCountryByPreSubmissionConfigurationId(
					batchNotificationDetail.id,
					batchNotificationDetail.partitionKey
				);
				setIsCountryDetailLoaded(true);
				if (res.responseObj) {
					handleCountryDetailAPIResponse(res.responseObj);
				} else {
					Shared.showSnackbarMessage(strings.countryDetailErrorMessage);
				}
			} catch (err) {
				setIsCountryDetailLoaded(true);
				Shared.showSnackbarMessage(strings.countryDetailErrorMessage);
			}
		};
		if (isBatchMessageTypeSMS() && !isCountryDetailLoaded) {
			getCountryByPreSubmissionConfigurationId();
		}
		return () => {
			setIsCountryDetailLoaded(true);
		};
		// disabled eslint rule as API needs to be called once on page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onPhoneNumberChangeHandler = (
		event: any,
		values: PhoneNumbers | null
	) => {
		if (isReadOnly) {
			return;
		}
		setFromNumber(values);
		messageSenderDetailChange(
			values && values.phoneNumber ? values && values.phoneNumber : "",
			false
		);
	};

	const onAlphaNumericFromNumberChange = (event: any) => {
		// regex to validate max 11 characters, starting with a letter, spaces, underscore(_), dash(-) allowed
		const validationRegex = /^([a-zA-Z])([a-zA-Z0-9-_ ]{0,10})$/;
		if (validationRegex.test(event.target.value) || event.target.value === "") {
			setAlphaNumericFromNumber(event.target.value as string);
			messageSenderDetailChange(event.target.value, true);
		}
	};

	const onIsAlphaNumericSenderIdChange = (event: any) => {
		if (isReadOnly) {
			return;
		}
		const fromNumberType = event.target.value === "true" ? true : false;
		messageSenderDetailChange(
			fromNumberType === true
				? alphaNumericFromNumber
				: fromNumber?.phoneNumber!,
			fromNumberType
		);
	};

	const getShowPhoneNumberList = () => {
		return (
			(!isBatchMessageTypeSMS() && !isBatchMessageTypeWhatsApp) ||
			(isBatchMessageTypeSMS() &&
				!batchNotificationDetail.isAlphaNumericSenderId)
		);
	};

	const getInfoMessage = () => {
		return noPhoneNumberAvailable
			? strings.noPhoneNumberAvailableMessage
			: countryDetail.alphanumericSupport ===
					AlphanumericSupport.RegistrationRequired &&
			  batchNotificationDetail.isAlphaNumericSenderId
			? strings.senderIdRegistrationRequiredMessage
			: "";
	};

	const shouldFocus = (
		callingElement: "From Number" | "Alphanumeric SenderId"
	) => {
		//only focus if highlight detail is true
		if (highlightRequiredDetail === false) {
			return false;
		}
		if (!batchNotificationDetail.fromNumber) {
			//error
			return false;
		}
		//returns true if should focus element
		if (batchNotificationDetail.country.length <= 0) {
			//returns true if country name calling function
			if (callingElement === "From Number") {
				setHasStep5ConfigChanged(true);
				return true;
			}
		} else if (batchNotificationDetail.title.length <= 0) {
			//returns true and should focus if calling component was message title
			if (callingElement === "Alphanumeric SenderId") {
				setHasStep5ConfigChanged(true);
				return true;
			}
		} else {
			return false;
		}

		return false;
	};

	const handleFromNumberChange = (event: any) => {
		setAlphaNumericFromNumber(event.target.value as string);
		messageSenderDetailChange(event.target.value, false);
	};

	const showInfoMessage = ((batchNotificationDetail.africasTalkingEnabled &&
		!isReadOnly) ||
		getInfoMessage()) && (
		<>
			<Grid item xs={12} data-testid="infoMessage">
				<div className="infoMessage">
					{batchNotificationDetail.africasTalkingEnabled && !isReadOnly ? (
						<>
							<span>{strings.africasTalkingInfoMessage} </span>
							<LinkPrimary
								href="https://account.africastalking.com/apps/sandbox/"
								target="blank"
							>
								https://account.africastalking.com/apps/sandbox/
							</LinkPrimary>
						</>
					) : (
						getInfoMessage()
					)}
				</div>
			</Grid>
		</>
	);

	return (
		<>
			<div className="batchMessageType" data-testid="batchMessageType">
				<Card variant="outlined">
					<CardContent>
						<Grid container spacing={1}>
							{countryDetail.vodafoneProviderEnabled ? (
								<>
									<Grid item xs={12} data-testid="fromNumberType">
										<RadioGroup row value={true}>
											<FormControlLabel
												value={true}
												control={<Radio color="primary" />}
												label={strings.vodafoneSenderIdLabel}
												data-testid="phoneNumberRadio"
												disabled={true}
											/>
										</RadioGroup>
									</Grid>
									<Grid item xs={6}>
										<TextField
											required
											label={strings.phoneNumberLabel}
											value={fromNumber}
											onChange={handleFromNumberChange}
											className="fromNumberTextField"
											helperText={
												!isReadOnly && highlightRequiredDetail
													? strings.formatError
													: ""
											}
											error={!isReadOnly && highlightRequiredDetail}
											fullWidth
											InputProps={{ readOnly: isReadOnly }}
											disabled={isReadOnly}
											data-testid="ATFromNumberInput"
										/>
									</Grid>
								</>
							) : (
								<>
									{!batchNotificationDetail.africasTalkingEnabled && (
										<>
											{isBatchMessageTypeSMS() && (
												<Grid item xs={12} data-testid="fromNumberType">
													<RadioGroup
														row
														value={
															batchNotificationDetail.isAlphaNumericSenderId
														}
														onChange={onIsAlphaNumericSenderIdChange}
													>
														<FormControlLabel
															value={true}
															control={<Radio color="primary" />}
															label={strings.alphanumericSenderIdLabel}
															disabled={
																countryDetail.alphanumericSupport ===
																	AlphanumericSupport.None || isReadOnly
															}
															data-testid="alphanumericRadio"
														/>
														<FormControlLabel
															value={false}
															control={<Radio color="primary" />}
															label={strings.phoneNumberLabel}
															data-testid="phoneNumberRadio"
															disabled={
																isReadOnly ||
																countryDetail.vodafoneProviderEnabled
															}
														/>
													</RadioGroup>
												</Grid>
											)}
											{isAlphanumericSenderIdSet() && (
												<>
													<Grid item xs={6}>
														<TextField
															required
															label={strings.alphanumericSenderIdLabel}
															value={alphaNumericFromNumber}
															onChange={onAlphaNumericFromNumberChange}
															className="fromNumberTextField"
															helperText={
																!isReadOnly &&
																!alphaNumericFromNumber &&
																highlightRequiredDetail
																	? strings.fromNumberRequiredMessage
																	: ""
															}
															error={
																!isReadOnly &&
																highlightRequiredDetail &&
																!alphaNumericFromNumber
															}
															fullWidth
															InputProps={{ readOnly: isReadOnly }}
															disabled={isReadOnly}
															data-testid="alphanumericInput"
															inputRef={(input) =>
																input &&
																shouldFocus("Alphanumeric SenderId") &&
																input.focus()
															}
														/>
													</Grid>
													<Box mt={3}>
														<Tooltip
															title={strings.alphaNumericSenderIdInfoTooltip}
															placement="top"
														>
															<Icon color="primary">info</Icon>
														</Tooltip>
													</Box>
												</>
											)}
											{getShowPhoneNumberList() && (
												<Grid item xs={6}>
													<FormControl
														fullWidth
														error={highlightRequiredDetail && !fromNumber}
													>
														<Autocomplete
															onChange={onPhoneNumberChangeHandler}
															options={phoneNumberList}
															value={fromNumber}
															className="fromNumberSelection"
															disableClearable={isReadOnly}
															getOptionLabel={(option: PhoneNumbers) =>
																option.phoneNumber +
																" (" +
																option.friendlyName +
																")"
															}
															autoHighlight
															getOptionSelected={(option, value) =>
																!value.friendlyName ||
																(option.friendlyName === value.friendlyName &&
																	!value.phoneNumber) ||
																option.phoneNumber === value.phoneNumber
															}
															data-testid="fromNumber"
															getOptionDisabled={() => isReadOnly}
															renderOption={(option: PhoneNumbers) => (
																<React.Fragment>
																	{option.phoneNumber +
																		" (" +
																		option.friendlyName +
																		")"}
																</React.Fragment>
															)}
															renderInput={(params) => (
																<TextField
																	value={params}
																	{...params}
																	label={strings.selectPhoneNumberLabel}
																	error={
																		!isReadOnly &&
																		highlightRequiredDetail &&
																		!fromNumber?.phoneNumber
																	}
																	helperText={
																		!isReadOnly &&
																		!fromNumber?.phoneNumber &&
																		highlightRequiredDetail
																			? strings.fromNumberRequiredMessage
																			: ""
																	}
																	data-testid="fromNumberInput"
																	disabled={isReadOnly}
																	inputRef={(input) =>
																		input &&
																		shouldFocus("From Number") &&
																		input.focus()
																	}
																/>
															)}
														/>
													</FormControl>
												</Grid>
											)}

											{isBatchMessageTypeWhatsApp && (
												<Grid item xs={6}>
													<TextField
														required
														label={strings.whatsappEnabledPhoneNumberLabel}
														value={whatsAppFromNumber}
														className="fromNumberTextField"
														helperText={
															!isReadOnly &&
															!whatsAppFromNumber &&
															highlightRequiredDetail
																? strings.whatsappEnabledPhoneNumberError
																: ""
														}
														error={
															!isReadOnly &&
															highlightRequiredDetail &&
															!whatsAppFromNumber
														}
														fullWidth
														InputProps={{
															readOnly: true,
														}}
														disabled={isReadOnly || !whatsAppFromNumber}
														data-testid="WhatsAppFromNumberInput"
													/>
												</Grid>
											)}
										</>
									)}
									{batchNotificationDetail.africasTalkingEnabled && (
										<>
											<Grid item xs={6}>
												<TextField
													required
													label={strings.phoneNumberLabel}
													value={alphaNumericFromNumber}
													onChange={handleFromNumberChange}
													className="fromNumberTextField"
													helperText={
														!isReadOnly &&
														!alphaNumericFromNumber &&
														highlightRequiredDetail
															? strings.fromNumberRequiredMessage
															: ""
													}
													error={
														!isReadOnly &&
														highlightRequiredDetail &&
														!alphaNumericFromNumber
													}
													fullWidth
													InputProps={{ readOnly: isReadOnly }}
													disabled={isReadOnly}
													data-testid="ATFromNumberInput"
												/>
											</Grid>
											{showInfoMessage}
										</>
									)}
								</>
							)}
						</Grid>
					</CardContent>
				</Card>
			</div>
		</>
	);
};
